import { useState, useMemo, useEffect } from 'react';
import { createRebus, updateRebus } from '../../../services/rebusService';

const createEmptyStation = () => ({
  name: '',
  description: '',
  questions: [createEmptyQuestion()],
  challenge: null,
});

const createEmptyQuestion = () => ({
  text: '',
  type: 'text',
  correctAnswer: '',
  answers: [],
  points: 1,
});

const createEmptyMCQuestion = () => ({
  text: '',
  type: 'multiple_choice',
  correctAnswer: null,
  answers: [
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ],
  points: 1,
});

const createEmptyChallenge = () => ({
  title: '',
  description: '',
  scoringType: 'points',
  maxPoints: 5,
});

const createEmptyExtraTask = () => ({
  name: '',
  text: '',
  type: 'photo',
  correctAnswer: '',
  points: 200,
});

const POINT_OPTIONS = [100, 200, 500, 1000];

export default function RebusCreator({ onSave, onClose, existingRebus = null }) {
  const isEditing = !!existingRebus;
  const [title, setTitle] = useState(existingRebus?.title || '');
  const [description, setDescription] = useState(existingRebus?.description || '');
  const [stations, setStations] = useState(() => {
    if (existingRebus?.stations?.length > 0) {
      return existingRebus.stations.map(s => ({
        name: s.name || '',
        description: s.description || '',
        questions: s.questions?.length > 0
          ? s.questions.map(q => ({
              text: q.text || '',
              type: q.type || 'text',
              correctAnswer: q.correctAnswer || '',
              answers: q.answers || [],
              points: q.points || 1,
            }))
          : [createEmptyQuestion()],
        challenge: s.challenge
          ? {
              title: s.challenge.title || '',
              description: s.challenge.description || '',
              scoringType: s.challenge.scoringType || 'points',
              maxPoints: s.challenge.maxPoints || 5,
            }
          : null,
      }));
    }
    return [createEmptyStation()];
  });
  const [extraTasks, setExtraTasks] = useState(() => {
    if (existingRebus?.extraTasks?.length > 0) {
      return existingRebus.extraTasks.map(t => ({
        name: t.name || '',
        text: t.text || '',
        type: t.type || 'text',
        correctAnswer: t.correctAnswer || '',
        points: t.points || 200,
      }));
    }
    return [];
  });
  const [deadline, setDeadline] = useState(existingRebus?.settings?.deadline || '');
  const [latePenalty, setLatePenalty] = useState(existingRebus?.settings?.latePointsPenalty ?? 5);
  const [taskOrder, setTaskOrder] = useState(existingRebus?.settings?.taskOrder || 'sequential');
  const [manualScoring, setManualScoring] = useState(existingRebus?.settings?.manualScoring || false);
  const [predefinedTeamsText, setPredefinedTeamsText] = useState(
    (existingRebus?.predefinedTeams || []).join('\n')
  );

  const [expandedStation, setExpandedStation] = useState(0);
  const [showStations, setShowStations] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // GooseChase-style mission editor state
  const [selectedTaskIndex, setSelectedTaskIndex] = useState(null);
  const [taskSearch, setTaskSearch] = useState('');
  const [mobileEditOpen, setMobileEditOpen] = useState(false);

  // Points overview calculation
  const pointsOverview = useMemo(() => {
    const counts = {};
    extraTasks.forEach(t => {
      const pts = t.points || 0;
      counts[pts] = (counts[pts] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([pts, count]) => ({ points: Number(pts), count }))
      .sort((a, b) => a.points - b.points);
  }, [extraTasks]);

  const totalPoints = useMemo(
    () => extraTasks.reduce((sum, t) => sum + (Number(t.points) || 0), 0),
    [extraTasks]
  );

  // Filtered tasks for search
  const filteredTasks = useMemo(() => {
    if (!taskSearch.trim()) return extraTasks.map((t, i) => ({ ...t, _origIndex: i }));
    const q = taskSearch.toLowerCase();
    return extraTasks
      .map((t, i) => ({ ...t, _origIndex: i }))
      .filter(t =>
        (t.name || '').toLowerCase().includes(q) ||
        (t.text || '').toLowerCase().includes(q)
      );
  }, [extraTasks, taskSearch]);

  // -- Station handlers --
  const addStation = () => {
    setStations([...stations, createEmptyStation()]);
    setExpandedStation(stations.length);
  };

  const removeStation = (index) => {
    if (stations.length <= 1) return;
    const updated = stations.filter((_, i) => i !== index);
    setStations(updated);
    if (expandedStation >= updated.length) {
      setExpandedStation(updated.length - 1);
    }
  };

  const updateStation = (index, field, value) => {
    const updated = [...stations];
    updated[index] = { ...updated[index], [field]: value };
    setStations(updated);
  };

  // -- Question handlers --
  const addQuestion = (stationIndex, type = 'text') => {
    const updated = [...stations];
    const newQ = type === 'multiple_choice' ? createEmptyMCQuestion() : createEmptyQuestion();
    updated[stationIndex].questions = [...updated[stationIndex].questions, newQ];
    setStations(updated);
  };

  const removeQuestion = (stationIndex, qIndex) => {
    const updated = [...stations];
    if (updated[stationIndex].questions.length <= 1) return;
    updated[stationIndex].questions = updated[stationIndex].questions.filter((_, i) => i !== qIndex);
    setStations(updated);
  };

  const updateQuestion = (stationIndex, qIndex, field, value) => {
    const updated = [...stations];
    updated[stationIndex].questions[qIndex] = {
      ...updated[stationIndex].questions[qIndex],
      [field]: value,
    };
    setStations(updated);
  };

  const changeQuestionType = (stationIndex, qIndex, newType) => {
    const updated = [...stations];
    const oldQ = updated[stationIndex].questions[qIndex];
    if (newType === 'multiple_choice') {
      updated[stationIndex].questions[qIndex] = {
        ...createEmptyMCQuestion(),
        text: oldQ.text,
        points: oldQ.points,
      };
    } else {
      updated[stationIndex].questions[qIndex] = {
        ...createEmptyQuestion(),
        text: oldQ.text,
        points: oldQ.points,
      };
    }
    setStations(updated);
  };

  // -- MC answer handlers --
  const updateMCAnswer = (stationIndex, qIndex, aIndex, field, value) => {
    const updated = [...stations];
    const answers = [...updated[stationIndex].questions[qIndex].answers];
    if (field === 'isCorrect' && value === true) {
      answers.forEach((a, i) => { answers[i] = { ...a, isCorrect: i === aIndex }; });
    } else {
      answers[aIndex] = { ...answers[aIndex], [field]: value };
    }
    updated[stationIndex].questions[qIndex] = {
      ...updated[stationIndex].questions[qIndex],
      answers,
    };
    setStations(updated);
  };

  // -- Challenge handlers --
  const toggleChallenge = (stationIndex) => {
    const updated = [...stations];
    if (updated[stationIndex].challenge) {
      updated[stationIndex].challenge = null;
    } else {
      updated[stationIndex].challenge = createEmptyChallenge();
    }
    setStations(updated);
  };

  const updateChallenge = (stationIndex, field, value) => {
    const updated = [...stations];
    updated[stationIndex].challenge = {
      ...updated[stationIndex].challenge,
      [field]: value,
    };
    setStations(updated);
  };

  // -- Extra task handlers --
  const addExtraTask = () => {
    const newTask = createEmptyExtraTask();
    const newIndex = extraTasks.length;
    setExtraTasks([...extraTasks, newTask]);
    setSelectedTaskIndex(newIndex);
    setMobileEditOpen(true);
  };

  const removeExtraTask = (index) => {
    setExtraTasks(extraTasks.filter((_, i) => i !== index));
    if (selectedTaskIndex === index) {
      setSelectedTaskIndex(null);
      setMobileEditOpen(false);
    } else if (selectedTaskIndex !== null && selectedTaskIndex > index) {
      setSelectedTaskIndex(selectedTaskIndex - 1);
    }
  };

  const updateExtraTask = (index, field, value) => {
    const updated = [...extraTasks];
    updated[index] = { ...updated[index], [field]: value };
    setExtraTasks(updated);
  };

  const selectTask = (origIndex) => {
    setSelectedTaskIndex(origIndex);
    setMobileEditOpen(true);
  };

  const closeMobileEdit = () => {
    setMobileEditOpen(false);
  };

  // -- Save --
  const handleSave = async () => {
    if (!title.trim()) {
      setSaveMessage({ type: 'error', text: 'Mikkelchasen treng ein tittel' });
      return;
    }

    if (stations.some(s => !s.name.trim())) {
      setSaveMessage({ type: 'error', text: 'Alle stasjonar treng eit namn' });
      return;
    }

    setIsSubmitting(true);
    setSaveMessage(null);

    try {
      const rebusData = {
        title: title.trim(),
        description: description.trim(),
        stations: stations.map((s, i) => ({
          name: s.name.trim(),
          description: s.description.trim(),
          order: i + 1,
          questions: s.questions.map(q => ({
            text: q.text.trim(),
            type: q.type,
            correctAnswer: q.type === 'text' ? (q.correctAnswer?.trim() || null) : null,
            answers: q.type === 'multiple_choice' ? q.answers : [],
            points: Number(q.points) || 1,
          })),
          challenge: s.challenge
            ? {
                title: s.challenge.title.trim(),
                description: s.challenge.description.trim(),
                scoringType: s.challenge.scoringType,
                maxPoints: Number(s.challenge.maxPoints) || 5,
              }
            : null,
        })),
        extraTasks: extraTasks.map(t => ({
          name: (t.name || '').trim(),
          text: (t.text || '').trim(),
          type: t.type,
          correctAnswer: t.correctAnswer?.trim() || null,
          points: Number(t.points) || 200,
        })),
        settings: {
          deadline: deadline || null,
          latePointsPenalty: Number(latePenalty) || 0,
          taskOrder: taskOrder,
          manualScoring: manualScoring,
        },
        predefinedTeams: predefinedTeamsText.trim()
          ? predefinedTeamsText.trim().split('\n').map(s => s.trim()).filter(Boolean)
          : [],
      };

      let savedRebus;
      if (isEditing) {
        await updateRebus(existingRebus.id, rebusData);
        savedRebus = { ...existingRebus, ...rebusData };
      } else {
        savedRebus = await createRebus(rebusData);
      }

      setSaveMessage({ type: 'success', text: isEditing ? 'Mikkelchase oppdatert!' : 'Mikkelchase lagra!' });

      if (onSave) {
        setTimeout(() => onSave(savedRebus), 800);
      }
    } catch (error) {
      console.error('Error saving rebus:', error);
      setSaveMessage({ type: 'error', text: error.message || 'Klarte ikkje lagre Mikkelchase' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedTask = selectedTaskIndex !== null ? extraTasks[selectedTaskIndex] : null;

  return (
    <div className="min-h-screen bg-background-light font-display text-[#0d121b] pb-32">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-light/95 backdrop-blur-sm shadow-ambient">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onClose}
            className="flex items-center gap-1 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            <span className="text-sm font-medium">Tilbake</span>
          </button>
          <h1 className="text-lg font-bold">{isEditing ? 'Rediger Mikkelchase' : 'Ny Mikkelchase'}</h1>
          <div className="w-16" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Save Message */}
        {saveMessage && (
          <div className={`p-3 rounded-xl text-sm font-medium ${
            saveMessage.type === 'error'
              ? 'bg-red-50 text-red-700'
              : 'bg-emerald-50 text-emerald-700'
          }`}>
            {saveMessage.text}
          </div>
        )}

        {/* Title & Description */}
        <div className="space-y-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Namn pa Mikkelchasen..."
            className="w-full text-2xl font-bold bg-transparent border-none outline-none placeholder:text-gray-300 text-[#0d121b]"
          />
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Kort skildring (valfritt)"
            className="w-full text-base bg-transparent border-none outline-none placeholder:text-gray-400 text-gray-600"
          />
        </div>

        {/* ====== STATIONS SECTION (collapsible) ====== */}
        <div className="space-y-3">
          <button
            onClick={() => setShowStations(!showStations)}
            className="w-full flex items-center justify-between py-3"
          >
            <h2 className="text-lg font-bold flex items-center gap-2">
              <span className="material-symbols-outlined text-emerald-600">pin_drop</span>
              Stasjonar
              <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full">
                {stations.length}
              </span>
            </h2>
            <span className={`material-symbols-outlined text-gray-400 transition-transform ${showStations ? 'rotate-180' : ''}`}>
              expand_more
            </span>
          </button>

          {showStations && (
            <div className="space-y-3">
              {stations.map((station, sIndex) => (
                <StationAccordion
                  key={sIndex}
                  station={station}
                  index={sIndex}
                  isExpanded={expandedStation === sIndex}
                  onToggle={() => setExpandedStation(expandedStation === sIndex ? -1 : sIndex)}
                  onUpdate={(field, value) => updateStation(sIndex, field, value)}
                  onRemove={() => removeStation(sIndex)}
                  onAddQuestion={(type) => addQuestion(sIndex, type)}
                  onRemoveQuestion={(qIndex) => removeQuestion(sIndex, qIndex)}
                  onUpdateQuestion={(qIndex, field, value) => updateQuestion(sIndex, qIndex, field, value)}
                  onChangeQuestionType={(qIndex, newType) => changeQuestionType(sIndex, qIndex, newType)}
                  onUpdateMCAnswer={(qIndex, aIndex, field, value) => updateMCAnswer(sIndex, qIndex, aIndex, field, value)}
                  onToggleChallenge={() => toggleChallenge(sIndex)}
                  onUpdateChallenge={(field, value) => updateChallenge(sIndex, field, value)}
                  canRemove={stations.length > 1}
                />
              ))}

              <button
                onClick={addStation}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors font-medium"
              >
                <span className="material-symbols-outlined text-[20px]">add</span>
                Legg til stasjon
              </button>
            </div>
          )}
        </div>

        {/* ====== GOOSECHASE-STYLE MISSIONS SECTION ====== */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <span className="material-symbols-outlined text-amber-500">star</span>
            Oppgaver
            {extraTasks.length > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">
                {extraTasks.length}
              </span>
            )}
          </h2>

          {/* Desktop: two-column layout. Mobile: list + slide-in modal */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* LEFT: Mission list */}
            <div className="flex-1 min-w-0 space-y-3">
              {/* Search */}
              <div className="relative">
                <span className="material-symbols-outlined text-[18px] text-gray-400 absolute left-3 top-1/2 -translate-y-1/2">
                  search
                </span>
                <input
                  type="text"
                  value={taskSearch}
                  onChange={(e) => setTaskSearch(e.target.value)}
                  placeholder="Sok i oppgaver..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white ghost-border ghost-border-focus rounded-xl text-sm"
                />
              </div>

              {/* Add mission button */}
              <button
                onClick={addExtraTask}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#0d121b] hover:bg-[#1a2233] text-white rounded-xl font-bold text-sm transition-colors"
              >
                <span className="material-symbols-outlined text-[20px]">add</span>
                Legg til oppgave
              </button>

              {/* Mission list */}
              {filteredTasks.length === 0 && extraTasks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <span className="material-symbols-outlined text-5xl mb-2">assignment</span>
                  <p className="font-medium text-sm">Ingen oppgaver enno</p>
                  <p className="text-xs text-gray-400 mt-1">Trykk knappen ovanfor for a legge til</p>
                </div>
              )}

              {filteredTasks.length === 0 && extraTasks.length > 0 && taskSearch.trim() && (
                <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                  <span className="material-symbols-outlined text-3xl mb-2">search_off</span>
                  <p className="font-medium text-sm">Ingen treff</p>
                </div>
              )}

              <div className="space-y-1">
                {filteredTasks.map((task) => {
                  const origIndex = task._origIndex;
                  const isSelected = selectedTaskIndex === origIndex;
                  return (
                    <button
                      key={origIndex}
                      onClick={() => selectTask(origIndex)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                        isSelected
                          ? 'bg-emerald-50 border-2 border-emerald-300 shadow-sm'
                          : 'bg-white shadow-ambient hover:shadow-lg'
                      }`}
                    >
                      {/* Type icon */}
                      <div className={`flex items-center justify-center size-9 rounded-lg shrink-0 ${
                        task.type === 'photo'
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-purple-50 text-purple-600'
                      }`}>
                        <span className="material-symbols-outlined text-[20px]">
                          {task.type === 'photo' ? 'photo_camera' : 'edit_note'}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-[#0d121b] truncate">
                          {task.name || `Oppgave ${origIndex + 1}`}
                        </p>
                        {task.text && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {task.text}
                          </p>
                        )}
                      </div>

                      {/* Points badge */}
                      <span className="shrink-0 bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-1 rounded-full">
                        {task.points}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: Edit panel (desktop only, sticky) */}
            <div className="hidden lg:block w-96 shrink-0">
              <div className="sticky top-20 space-y-4">
                {selectedTask ? (
                  <MissionEditPanel
                    task={selectedTask}
                    index={selectedTaskIndex}
                    onUpdate={(field, value) => updateExtraTask(selectedTaskIndex, field, value)}
                    onRemove={() => removeExtraTask(selectedTaskIndex)}
                    onClose={() => setSelectedTaskIndex(null)}
                  />
                ) : (
                  <div className="bg-white rounded-2xl shadow-ambient p-8 flex flex-col items-center justify-center text-gray-400">
                    <span className="material-symbols-outlined text-4xl mb-2">touch_app</span>
                    <p className="font-medium text-sm">Vel ei oppgave for a redigere</p>
                  </div>
                )}

                {/* Task order setting */}
                <div className="bg-white rounded-2xl shadow-ambient p-4 space-y-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Oppgaverekkefolgje for deltakarar
                  </label>
                  <select
                    value={taskOrder}
                    onChange={(e) => setTaskOrder(e.target.value)}
                    className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm bg-white"
                  >
                    <option value="sequential">Kronologisk</option>
                    <option value="random">Tilfeldig</option>
                  </select>
                </div>

                {/* Points overview */}
                {extraTasks.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-ambient p-4 space-y-3">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Poengoversikt
                    </h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-gray-400 uppercase tracking-wider">
                          <th className="text-left pb-2 font-medium">Poeng</th>
                          <th className="text-right pb-2 font-medium"># Oppgaver</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pointsOverview.map(({ points, count }) => (
                          <tr key={points} className="border-t border-gray-50">
                            <td className="py-1.5 font-medium text-gray-700">{points}</td>
                            <td className="py-1.5 text-right text-gray-500">{count}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-200">
                          <td className="pt-2 font-bold text-[#0d121b]">Totalt</td>
                          <td className="pt-2 text-right font-bold text-amber-600">{totalPoints} poeng</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile task order + points overview (below list) */}
          <div className="lg:hidden space-y-3">
            <div className="bg-white rounded-2xl shadow-ambient p-4 space-y-2">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
                Oppgaverekkefolgje for deltakarar
              </label>
              <select
                value={taskOrder}
                onChange={(e) => setTaskOrder(e.target.value)}
                className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm bg-white"
              >
                <option value="sequential">Kronologisk</option>
                <option value="random">Tilfeldig</option>
              </select>
            </div>

            {extraTasks.length > 0 && (
              <div className="bg-white rounded-2xl shadow-ambient p-4 space-y-3">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Poengoversikt
                </h3>
                <div className="flex flex-wrap gap-2">
                  {pointsOverview.map(({ points, count }) => (
                    <div key={points} className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5">
                      <span className="text-sm font-bold text-gray-700">{points}</span>
                      <span className="text-xs text-gray-400">x{count}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm font-bold text-gray-600">Totalt</span>
                  <span className="text-sm font-bold text-amber-600">{totalPoints} poeng</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ====== SETTINGS SECTION ====== */}
        <div className="space-y-3">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="w-full flex items-center justify-between py-3"
          >
            <h2 className="text-lg font-bold flex items-center gap-2">
              <span className="material-symbols-outlined text-gray-500">settings</span>
              Innstillingar
            </h2>
            <span className={`material-symbols-outlined text-gray-400 transition-transform ${showSettings ? 'rotate-180' : ''}`}>
              expand_more
            </span>
          </button>

          {showSettings && (
            <div className="bg-white rounded-2xl shadow-ambient p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tidsfrist (valfritt)
                </label>
                <input
                  type="datetime-local"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Straffepoeng for sein levering
                </label>
                <input
                  type="number"
                  value={latePenalty}
                  onChange={(e) => setLatePenalty(e.target.value)}
                  min="0"
                  className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm"
                />
              </div>

              {/* Manual scoring toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-gray-700">Manuell poenggiving</p>
                  <p className="text-xs text-gray-500">Admin godkjenner alle svar manuelt (ingen auto-scoring)</p>
                </div>
                <button
                  type="button"
                  onClick={() => setManualScoring(!manualScoring)}
                  className={`relative w-12 h-7 rounded-full transition-colors ${manualScoring ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${manualScoring ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Predefined teams */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Predefinerte lagnamn (valfritt)
                </label>
                <p className="text-xs text-gray-500 mb-2">Eitt lagnamn per linje. Når sett, får deltakarar ein dropdown i staden for fri tekst.</p>
                <textarea
                  value={predefinedTeamsText}
                  onChange={(e) => setPredefinedTeamsText(e.target.value)}
                  placeholder={"Mikkel\nDJ Papegøy\nMinken\nMikkeline\nSolbjørn"}
                  rows={5}
                  className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm resize-none"
                />
                {predefinedTeamsText.trim() && (
                  <p className="text-xs text-emerald-600 mt-1 font-medium">
                    {predefinedTeamsText.trim().split('\n').filter(s => s.trim()).length} lag definerte
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Edit Modal (slide-up panel) */}
      {mobileEditOpen && selectedTask && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeMobileEdit}
          />
          {/* Panel */}
          <div className="absolute bottom-0 left-0 right-0 bg-background-light rounded-t-3xl max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up">
            <div className="p-4">
              {/* Drag handle */}
              <div className="flex justify-center mb-4">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>
              <MissionEditPanel
                task={selectedTask}
                index={selectedTaskIndex}
                onUpdate={(field, value) => updateExtraTask(selectedTaskIndex, field, value)}
                onRemove={() => removeExtraTask(selectedTaskIndex)}
                onClose={closeMobileEdit}
                isMobile
              />
            </div>
          </div>
        </div>
      )}

      {/* Save Button (sticky bottom) */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-background-light/95 backdrop-blur-sm shadow-[0_-4px_16px_rgba(0,0,0,0.06)] p-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="w-full py-3.5 bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 disabled:bg-gray-300 text-white rounded-2xl font-bold text-base transition-all flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Lagrar...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[20px]">save</span>
                Lagre
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ====== MISSION EDIT PANEL ======
function MissionEditPanel({ task, index, onUpdate, onRemove, onClose, isMobile = false }) {
  const [customPointsInput, setCustomPointsInput] = useState('');
  const isCustomPoints = !POINT_OPTIONS.includes(Number(task.points));

  useEffect(() => {
    if (isCustomPoints) {
      setCustomPointsInput(String(task.points));
    } else {
      setCustomPointsInput('');
    }
  }, [index]);

  const handleCustomPointsChange = (val) => {
    setCustomPointsInput(val);
    const num = Number(val);
    if (num > 0) {
      onUpdate('points', num);
    }
  };

  const selectPointOption = (pts) => {
    setCustomPointsInput('');
    onUpdate('points', pts);
  };

  return (
    <div className="bg-white rounded-2xl shadow-ambient p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-base text-[#0d121b]">
          {isMobile ? `Rediger oppgave ${index + 1}` : `Oppgave ${index + 1}`}
        </h3>
        {isMobile && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <span className="material-symbols-outlined">close</span>
          </button>
        )}
      </div>

      {/* Task name */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Oppgavenamn
          </label>
          <span className="text-xs text-gray-400">
            {(task.name || '').length}/100
          </span>
        </div>
        <input
          type="text"
          value={task.name || ''}
          onChange={(e) => {
            if (e.target.value.length <= 100) onUpdate('name', e.target.value);
          }}
          placeholder="Gi oppgava eit namn..."
          className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm font-medium"
        />
      </div>

      {/* Task type */}
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
          Oppgavetype
        </label>
        <select
          value={task.type}
          onChange={(e) => onUpdate('type', e.target.value)}
          className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm bg-white"
        >
          <option value="photo">📷 Kamera</option>
          <option value="text">✏️ Tekst</option>
        </select>
      </div>

      {/* Points quick-select */}
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
          Poeng
        </label>
        <div className="flex flex-wrap gap-2">
          {POINT_OPTIONS.map(pts => (
            <button
              key={pts}
              type="button"
              onClick={() => selectPointOption(pts)}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                Number(task.points) === pts && !customPointsInput
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {pts}
            </button>
          ))}
          {/* Custom input */}
          <input
            type="number"
            value={customPointsInput}
            onChange={(e) => handleCustomPointsChange(e.target.value)}
            placeholder="Eigendefinert"
            min="1"
            className={`w-28 px-3 py-2 rounded-xl text-sm font-bold text-center border-2 transition-all focus:outline-none ${
              customPointsInput
                ? 'border-amber-400 bg-amber-50 text-amber-700'
                : 'border-gray-200 bg-gray-50 text-gray-500'
            }`}
          />
        </div>
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Beskriving
          </label>
          <span className="text-xs text-gray-400">
            {(task.text || '').length}/2000
          </span>
        </div>
        <textarea
          value={task.text || ''}
          onChange={(e) => {
            if (e.target.value.length <= 2000) onUpdate('text', e.target.value);
          }}
          placeholder="Beskriv kva deltakarane skal gjere..."
          rows={4}
          className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm resize-none"
        />
      </div>

      {/* Correct answer (text type only) */}
      {task.type === 'text' && (
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Rett svar (valfritt)
          </label>
          <input
            type="text"
            value={task.correctAnswer || ''}
            onChange={(e) => onUpdate('correctAnswer', e.target.value)}
            placeholder="Rett svar for auto-poeng"
            className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm"
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={() => {
            onRemove();
            if (isMobile) onClose();
          }}
          className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600 transition-colors font-medium"
        >
          <span className="material-symbols-outlined text-[18px]">delete</span>
          Slett oppgave
        </button>
        {isMobile && (
          <button
            onClick={onClose}
            className="ml-auto px-5 py-2 bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 text-white rounded-xl text-sm font-bold transition-all"
          >
            Lagre endringar
          </button>
        )}
      </div>
    </div>
  );
}

// ====== STATION ACCORDION (unchanged functionality) ======
function StationAccordion({
  station,
  index,
  isExpanded,
  onToggle,
  onUpdate,
  onRemove,
  onAddQuestion,
  onRemoveQuestion,
  onUpdateQuestion,
  onChangeQuestionType,
  onUpdateMCAnswer,
  onToggleChallenge,
  onUpdateChallenge,
  canRemove,
}) {
  return (
    <div className="bg-white rounded-2xl shadow-ambient overflow-hidden">
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center size-8 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm shrink-0">
            {index + 1}
          </div>
          <span className="font-bold text-[#0d121b] truncate">
            {station.name || `Stasjon ${index + 1}`}
          </span>
          <span className="text-xs text-gray-400">
            {station.questions.length} spm{station.challenge ? ' + utfordring' : ''}
          </span>
        </div>
        <span className={`material-symbols-outlined text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
          expand_more
        </span>
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 pt-4">
          {/* Station Name & Description */}
          <div className="space-y-2">
            <input
              type="text"
              value={station.name}
              onChange={(e) => onUpdate('name', e.target.value)}
              placeholder="Stasjonsnamn"
              className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm font-medium"
            />
            <input
              type="text"
              value={station.description}
              onChange={(e) => onUpdate('description', e.target.value)}
              placeholder="Skildring (valfritt)"
              className="w-full px-3 py-2.5 ghost-border ghost-border-focus rounded-xl text-sm"
            />
          </div>

          {/* Questions */}
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wide">Sporsmal</h4>
            {station.questions.map((q, qIndex) => (
              <QuestionCard
                key={qIndex}
                question={q}
                index={qIndex}
                onUpdate={(field, value) => onUpdateQuestion(qIndex, field, value)}
                onRemove={() => onRemoveQuestion(qIndex)}
                onChangeType={(newType) => onChangeQuestionType(qIndex, newType)}
                onUpdateMCAnswer={(aIndex, field, value) => onUpdateMCAnswer(qIndex, aIndex, field, value)}
                canRemove={station.questions.length > 1}
              />
            ))}

            <div className="flex gap-2">
              <button
                onClick={() => onAddQuestion('text')}
                className="flex-1 flex items-center justify-center gap-1 py-2 border border-dashed border-gray-200 rounded-xl text-xs text-gray-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors font-medium"
              >
                <span className="material-symbols-outlined text-[16px]">add</span>
                Tekstsporsmal
              </button>
              <button
                onClick={() => onAddQuestion('multiple_choice')}
                className="flex-1 flex items-center justify-center gap-1 py-2 border border-dashed border-gray-200 rounded-xl text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors font-medium"
              >
                <span className="material-symbols-outlined text-[16px]">add</span>
                Fleirval
              </button>
            </div>
          </div>

          {/* Challenge */}
          <div className="space-y-2">
            <button
              onClick={onToggleChallenge}
              className="flex items-center gap-2 text-sm font-bold text-gray-600"
            >
              <span className={`material-symbols-outlined text-[18px] ${station.challenge ? 'text-purple-600' : 'text-gray-400'}`}>
                {station.challenge ? 'check_box' : 'check_box_outline_blank'}
              </span>
              Utfordring pa stasjonen
            </button>

            {station.challenge && (
              <div className="bg-purple-50 rounded-xl p-3 space-y-2 border border-purple-100">
                <input
                  type="text"
                  value={station.challenge.title}
                  onChange={(e) => onUpdateChallenge('title', e.target.value)}
                  placeholder="Utfordrings-tittel"
                  className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
                <textarea
                  value={station.challenge.description}
                  onChange={(e) => onUpdateChallenge('description', e.target.value)}
                  placeholder="Skildring av utfordringa"
                  rows={2}
                  className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
                />
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-purple-700 font-medium">Poengtype</label>
                    <select
                      value={station.challenge.scoringType}
                      onChange={(e) => onUpdateChallenge('scoringType', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 bg-white"
                    >
                      <option value="points">Poeng</option>
                      <option value="ranking">Rangering</option>
                      <option value="pass_fail">Godkjent/ikkje</option>
                    </select>
                  </div>
                  <div className="w-24">
                    <label className="text-xs text-purple-700 font-medium">Maks poeng</label>
                    <input
                      type="number"
                      value={station.challenge.maxPoints}
                      onChange={(e) => onUpdateChallenge('maxPoints', e.target.value)}
                      min="1"
                      className="w-full mt-1 px-3 py-2 border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Remove Station */}
          {canRemove && (
            <button
              onClick={onRemove}
              className="flex items-center gap-1 text-sm text-red-500 hover:text-red-600 transition-colors font-medium"
            >
              <span className="material-symbols-outlined text-[16px]">delete</span>
              Fjern stasjon
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ====== QUESTION CARD (unchanged) ======
function QuestionCard({ question, index, onUpdate, onRemove, onChangeType, onUpdateMCAnswer, canRemove }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500">Sporsmal {index + 1}</span>
        <div className="flex items-center gap-1">
          <select
            value={question.type}
            onChange={(e) => onChangeType(e.target.value)}
            className="text-xs ghost-border ghost-border-focus rounded-lg px-2 py-1 bg-white"
          >
            <option value="text">Tekst</option>
            <option value="multiple_choice">Fleirval</option>
          </select>
          {canRemove && (
            <button
              onClick={onRemove}
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          )}
        </div>
      </div>

      <input
        type="text"
        value={question.text}
        onChange={(e) => onUpdate('text', e.target.value)}
        placeholder="Skriv sporsmalet..."
        className="w-full px-3 py-2 ghost-border ghost-border-focus rounded-lg text-sm"
      />

      {question.type === 'text' && (
        <input
          type="text"
          value={question.correctAnswer || ''}
          onChange={(e) => onUpdate('correctAnswer', e.target.value)}
          placeholder="Rett svar (valfritt, for auto-poeng)"
          className="w-full px-3 py-2 ghost-border ghost-border-focus rounded-lg text-sm"
        />
      )}

      {question.type === 'multiple_choice' && (
        <div className="space-y-1.5">
          {question.answers.map((a, aIndex) => (
            <div key={aIndex} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onUpdateMCAnswer(aIndex, 'isCorrect', true)}
                className={`shrink-0 size-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  a.isCorrect
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : 'border-gray-300 hover:border-emerald-400'
                }`}
              >
                {a.isCorrect && <span className="material-symbols-outlined text-[14px]">check</span>}
              </button>
              <input
                type="text"
                value={a.text}
                onChange={(e) => onUpdateMCAnswer(aIndex, 'text', e.target.value)}
                placeholder={`Svar ${aIndex + 1}`}
                className="flex-1 px-3 py-1.5 ghost-border ghost-border-focus rounded-lg text-sm"
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500">Poeng:</label>
        <input
          type="number"
          value={question.points}
          onChange={(e) => onUpdate('points', e.target.value)}
          min="1"
          className="w-16 px-2 py-1 ghost-border ghost-border-focus rounded-lg text-sm text-center"
        />
      </div>
    </div>
  );
}
